import React, { useEffect } from "react";
import { Box, Toolbar, useScrollTrigger, Fade } from "@mui/material";
import { useLocation, useNavigationType } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

export interface LayoutProps {
  children?: React.ReactNode;
  headerVariant?: "default" | "transparent" | "colored";
}

// Scroll to top handler
const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  useEffect(() => {
    // Hash targets (e.g. /contact#contact-form) handle their own scrolling
    if (navType === "PUSH" && !hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navType]);

  return (
    <Fade in={trigger}>
      <Box
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        role="button"
        aria-label="Scroll to top"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1000,
          cursor: "pointer",
          opacity: 0.7,
          "&:hover": {
            opacity: 1,
          },
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        {children}
      </Box>
    </Fade>
  );
};

const Layout: React.FC<LayoutProps> = ({
  children,
  headerVariant = "default",
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      <Header variant={headerVariant} />
      <Toolbar id="back-to-top-anchor" />

      {/* Pages own their sections/containers so heroes can run full-bleed */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Footer />

      <ScrollToTop>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 3,
            color: "primary.contrastText",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 11L12 6L17 11M12 18V7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </ScrollToTop>
    </Box>
  );
};

export default Layout;
